import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../../context/AuthContext';
import { formatOrderReference } from '../../../../utils/formatOrderReference';
import FedExPackagingPicker from '../../carriers/FedExPackagingPicker';
import {
  DEFAULT_FEDEX_PACKAGING_ID,
  getFedExPackagingOption,
  type FedExPackagingId,
} from '../../carriers/fedexPackagingCatalog';
import { isInternationalDestination } from '../../fulfillmentConfig';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';
const LB_PER_KG = 2.20462;

type BatchOrder = {
  id: string;
  status: string;
  deliverySpeed?: string;
  carrierServiceCode?: string | null;
  carrierServiceName?: string | null;
  addressVerification?: { status?: string } | null;
  addressSnapshot?: { city?: string; country?: string } | null;
  customer?: {
    email?: string;
    customerProfile?: { fullName?: string | null; country?: string | null } | null;
  } | null;
  items?: Array<{ qty: number; unit: string }>;
};

type RateOption = {
  provider: 'FEDEX' | 'UPS';
  serviceCode: string;
  serviceName: string;
  amountMinor: number;
  currency: string;
};

function estimateWeightLbs(items?: Array<{ qty: number; unit: string }>): number {
  if (!items?.length) return 2;
  const total = items.reduce((sum, item) => {
    const u = String(item.unit || '').toUpperCase();
    const qty = Number(item.qty || 0);
    if (u === 'KG') return sum + qty * LB_PER_KG;
    if (u === 'PACK') return sum + 2;
    return sum + qty;
  }, 0);
  return Math.max(0.5, Number(total.toFixed(1)));
}

function parseApiError(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'message' in body) {
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && typeof message[0] === 'string') return message[0];
  }
  return fallback;
}

type Props = {
  batchId: string;
  batchStatus: string;
  orders: BatchOrder[];
};

export default function CarrierBatchShipPanel({ batchId, batchStatus, orders }: Props) {
  const { t } = useTranslation();
  const { authorizedFetch, auth } = useAuth();
  const canShip =
    auth.type === 'ADMIN' || (auth.permissions || []).includes('CARRIER_SHIPMENT:CREATE');

  const eligibleOrders = useMemo(
    () =>
      orders.filter(
        (o) =>
          (o.status === 'PAID' || o.status === 'RESHIP_PENDING') &&
          o.addressVerification?.status === 'VERIFIED',
      ),
    [orders],
  );

  const [weightByOrderId, setWeightByOrderId] = useState<Record<string, string>>({});
  const [packagingType, setPackagingType] = useState<FedExPackagingId>(DEFAULT_FEDEX_PACKAGING_ID);
  const [serviceCode, setServiceCode] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [quotes, setQuotes] = useState<RateOption[]>([]);
  const [quoting, setQuoting] = useState(false);
  const [shipping, setShipping] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shippedIds, setShippedIds] = useState<Set<string>>(new Set());

  const batchIsInternational = useMemo(
    () =>
      eligibleOrders.some((o) =>
        isInternationalDestination(
          o.addressSnapshot?.country || o.customer?.customerProfile?.country,
        ),
      ),
    [eligibleOrders],
  );

  const maxPackagingLbs = useMemo(() => {
    const opt = getFedExPackagingOption(packagingType);
    return opt?.maxWeightLbs ?? 50;
  }, [packagingType]);

  const parcelPayload = (orderId: string) => [
    {
      sequenceIndex: 1,
      weightLbs: Math.min(
        maxPackagingLbs,
        Math.max(0.1, Number(weightByOrderId[orderId]) || 1),
      ),
      packagingType,
    },
  ];

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const o of eligibleOrders) {
      next[o.id] = String(estimateWeightLbs(o.items));
    }
    setWeightByOrderId(next);
  }, [eligibleOrders]);

  const loadShippedState = useCallback(async () => {
    const shipped = new Set<string>();
    await Promise.all(
      eligibleOrders.map(async (o) => {
        const res = await authorizedFetch(`${API_BASE}/carriers/shipments/orders/${o.id}`, {
          headers: { 'x-department-slug': '' },
        });
        if (!res.ok) return;
        const rows = (await res.json()) as Array<{ status: string }>;
        if (rows.some((r) => !['VOIDED', 'CANCELLED'].includes(r.status))) {
          shipped.add(o.id);
        }
      }),
    );
    setShippedIds(shipped);
  }, [authorizedFetch, eligibleOrders]);

  useEffect(() => {
    void loadShippedState();
  }, [loadShippedState]);

  const quoteFromFirstOrder = async () => {
    const first = eligibleOrders.find((o) => !shippedIds.has(o.id));
    if (!first) return;
    setQuoting(true);
    setError(null);
    try {
      const res = await authorizedFetch(
        `${API_BASE}/carriers/shipments/orders/${first.id}/quote`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
          body: JSON.stringify({ parcels: parcelPayload(first.id) }),
        },
      );
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(parseApiError(body, 'Quote failed'));
      const fedex = (body as RateOption[]).filter((q) => q.provider === 'FEDEX');
      const data = fedex.length ? fedex : (body as RateOption[]);
      if (!data.length) throw new Error('No FedEx rates available.');
      setQuotes(data);
      const preferred =
        (first.carrierServiceCode
          ? data.find((q) => q.serviceCode === first.carrierServiceCode)
          : null) || data[0];
      setServiceCode(preferred.serviceCode);
      setServiceName(preferred.serviceName);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Quote failed');
    } finally {
      setQuoting(false);
    }
  };

  const shipAll = async () => {
    if (!serviceCode || !canShip) return;
    setShipping(true);
    setError(null);
    setProgress(null);
    const pending = eligibleOrders.filter((o) => !shippedIds.has(o.id));
    let done = 0;
    for (const o of pending) {
      setProgress(`${done + 1} / ${pending.length}`);
      try {
        const res = await authorizedFetch(`${API_BASE}/carriers/shipments/orders/${o.id}/ship`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'x-department-slug': '' },
          body: JSON.stringify({
            provider: 'FEDEX',
            serviceCode,
            handoffMode: 'DROP_OFF',
            parcels: parcelPayload(o.id),
          }),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(parseApiError(body, `Failed for ${formatOrderReference(o.id)}`));
        }
        done += 1;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Batch ship failed');
        break;
      }
    }
    await loadShippedState();
    setShipping(false);
    setProgress(null);
  };

  if (batchStatus !== 'OPEN' && eligibleOrders.length === 0) return null;

  return (
    <section className="rounded-xl border border-sky-200 bg-sky-50/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-semibold text-slate-900">
          {t('backoffice.carrier_batch_ship_title', { defaultValue: 'FedEx labels for this batch' })}
        </h4>
        <span className="text-[11px] text-slate-600">
          {eligibleOrders.length} ready · {shippedIds.size} labeled
        </span>
      </div>
      <p className="mt-1 text-xs text-slate-600">
        {t('backoffice.carrier_batch_ship_subtitle', {
          defaultValue:
            'Choose the FedEx box size, set each shipment weight in pounds, pick a service, then create labels.',
        })}
      </p>

      {eligibleOrders.length === 0 ? (
        <p className="mt-3 text-xs text-slate-500">
          {t('backoffice.carrier_batch_no_eligible', {
            defaultValue: 'No paid orders with verified addresses in this batch.',
          })}
        </p>
      ) : (
        <>
          <div className="mt-3 max-w-md">
            <FedExPackagingPicker
              value={packagingType}
              onChange={setPackagingType}
              isInternational={batchIsInternational}
              disabled={shipping || quoting}
            />
          </div>
          <ul className="mt-3 max-h-48 space-y-1.5 overflow-y-auto">
            {eligibleOrders.map((o) => {
              const labeled = shippedIds.has(o.id);
              return (
                <li
                  key={o.id}
                  className="grid grid-cols-[minmax(0,1fr)_5rem_auto] items-center gap-2 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs"
                >
                  <div className="min-w-0 truncate">
                    <span className="font-mono font-semibold">{formatOrderReference(o.id)}</span>
                    <span className="ml-2 text-slate-500">
                      {(o.addressSnapshot as { city?: string })?.city || '—'}
                    </span>
                  </div>
                  <input
                    type="number"
                    min={0.1}
                    max={maxPackagingLbs}
                    step={0.1}
                    disabled={labeled || shipping}
                    className="rounded border border-slate-300 px-1.5 py-1 text-xs disabled:bg-slate-50"
                    value={weightByOrderId[o.id] ?? ''}
                    onChange={(e) =>
                      setWeightByOrderId((prev) => ({ ...prev, [o.id]: e.target.value }))
                    }
                  />
                  <span
                    className={[
                      'rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase',
                      labeled ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800',
                    ].join(' ')}
                  >
                    {labeled ? 'Labeled' : 'Ready'}
                  </span>
                </li>
              );
            })}
          </ul>

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <button
              type="button"
              disabled={quoting || shipping}
              onClick={() => void quoteFromFirstOrder()}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold disabled:opacity-50"
            >
              {quoting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Get FedEx rates
            </button>
            {serviceCode ? (
              <span className="text-xs text-slate-700">
                Service: <strong>{serviceName || serviceCode}</strong>
              </span>
            ) : null}
            {quotes.length > 1 ? (
              <select
                className="rounded border border-slate-300 px-2 py-1 text-xs"
                value={serviceCode}
                onChange={(e) => {
                  const code = e.target.value;
                  const match = quotes.find((q) => q.serviceCode === code);
                  setServiceCode(code);
                  setServiceName(match?.serviceName || code);
                }}
              >
                {quotes.map((q) => (
                  <option key={q.serviceCode} value={q.serviceCode}>
                    {q.serviceName} · {(q.amountMinor / 100).toFixed(2)}
                  </option>
                ))}
              </select>
            ) : null}
            {canShip ? (
              <button
                type="button"
                disabled={!serviceCode || shipping || eligibleOrders.every((o) => shippedIds.has(o.id))}
                onClick={() => void shipAll()}
                className="inline-flex h-8 items-center gap-1 rounded-lg bg-sky-700 px-3 text-xs font-semibold text-white disabled:opacity-50"
              >
                {shipping ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Package className="h-3.5 w-3.5" />}
                {shipping
                  ? progress || 'Shipping…'
                  : t('backoffice.carrier_batch_ship_all', { defaultValue: 'Create all labels' })}
              </button>
            ) : null}
          </div>
        </>
      )}
      {error ? <p className="mt-2 text-xs text-red-600">{error}</p> : null}
    </section>
  );
}
